import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { MyOwnService, MyOwnServiceDto, MyOwnServicesExplodeDetailsDTO } from '../interfaces/services/my-own-service-dto.interface';
import { map, shareReplay } from 'rxjs/operators';
import { DocumentTemplatePagedResult, DocumentTemplateParams } from 'src/app/shared/models/iCourrier/document-template-paged-result';
import { MeRightMailGlobal } from 'src/app/shared/models/iCourrier/me/right-mail-global';
import { RightManagement } from 'src/app/shared/models/iCourrier/right/right-management';
import { UserInfo } from 'src/app/shared/models/iCourrier/me/user-info';
import { PersonalParams, PersonalParamsParams } from 'src/app/shared/models/iCourrier/me/personal-params';
import { PersonalParamsDTO } from '../interfaces/me/personal-params-dto.interface';
import { NotifMailsDTO, NotifMailSearchDTOPaginate } from '../interfaces/me/notif-mails-dto.interface';
import { NotifMailParams } from 'src/app/shared/models/iCourrier/me/notification-mail';
import { NotificationMailParams } from 'src/app/shared/models/iCourrier/notification-mail';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class MeService {
  private iAdminURL: string;
  private iCourrierURL: string;
  private rightManagement$: Observable<RightManagement>;

  constructor(private http: HttpClient) {
    const config = environment;
    this.iAdminURL = config.iAdminApiBaseURL;
    this.iCourrierURL = config.apiICourrierBaseUrl;
  }

  public getUserServices(): Observable<{ userId: number; serviceId: number }[]> {
    return this.http.get<{ userId: number; serviceId: number }[]>(`${this.iAdminURL}/Me/UserServices`);
  }

  public fetchMyOwnServices(): Observable<MyOwnService[]> {
    return this.http.get<MyOwnServiceDto[]>(`${this.iCourrierURL}/Me/UserServiceDroits`).pipe(
      map((res: MyOwnServiceDto[]) => {
        const mappedServicesArray: MyOwnService[] = [];
        res.forEach((serviceRight: MyOwnServiceDto) => {
          let mappedService: MyOwnService = {} as MyOwnService;
          mappedService = { ...serviceRight, selectable: true };
          mappedServicesArray.push(mappedService);
        });
        return mappedServicesArray;
      })
    );
  }

  public getMeServicesExploses(): Observable<MyOwnServicesExplodeDetailsDTO[]> {
    return this.http.get<MyOwnServicesExplodeDetailsDTO[]>(`${this.iCourrierURL}/Me/ServicesExploseDetails`);
  }

  public getMyTemplates(data?: DocumentTemplateParams): Observable<DocumentTemplatePagedResult> {
    let params = new HttpParams();
    if (data) {
      Object.keys(data).forEach((key) => (params = params.set(key, data[key])));
    }
    return this.http.get<DocumentTemplatePagedResult>(`${this.iCourrierURL}/Global/Documents/Templates/Me/Search?${params}`);
  }

  public getUserInfo(): Observable<UserInfo> {
    return this.http.get<UserInfo>(`${this.iCourrierURL}/Me/UserContactInfo`);
  }

  //#region me Rights
  /**
   * Fetches the current user's management rights and caches the result for the app lifetime (it rarely changes during a session).
   */
  public rightManagement(): Observable<RightManagement> {
    if (!this.rightManagement$) {
      this.rightManagement$ = this.http.get<RightManagement>(`${this.iCourrierURL}/Me/Rights/Management`).pipe(shareReplay(1));
    }
    return this.rightManagement$;
  }

  public rightMailGlobal(): Observable<MeRightMailGlobal> {
    return this.http.get<MeRightMailGlobal>(`${this.iCourrierURL}/Me/Rights/Mails`);
  }

  //#endregion

  //#region Personal params
  // @param type Available values : CourrierFavoris, CourrierRechercheSauvegardee, CourrierCustomViews
  public getMyPersonalParams(type: string, name?: string): Observable<PersonalParamsDTO[]> {
    let params = new HttpParams();
    params = params.set('type', type);
    //*the name isn't mandatory so it can be null
    if (name) {
      params = params.set('name', name);
    }
    return this.http.get<PersonalParamsDTO[]>(`${this.iCourrierURL}/Me/Configurations/PersonalParams?${params}`).pipe(
      map((res) => {
        return res.map((x) => new PersonalParams(x));
      })
    );
  }

  // @param type Available values : CourrierFavoris, CourrierRechercheSauvegardee, CourrierCustomViews
  public postMyPersonalParams(type: string, data: PersonalParamsParams): Observable<PersonalParamsDTO> {
    return this.http.post<PersonalParamsDTO>(`${this.iCourrierURL}/Me/Configurations/PersonalParams?type=${type}`, data);
  }

  // @param type Available values : CourrierFavoris, CourrierRechercheSauvegardee, CourrierCustomViews
  public putMyPersonalParams(id: number, type: string, data: PersonalParamsParams): Observable<PersonalParamsDTO> {
    return this.http.put<PersonalParamsDTO>(`${this.iCourrierURL}/Me/Configurations/PersonalParams/${id}?type=${type}`, data);
  }

  // @param type Available values : CourrierFavoris, CourrierRechercheSauvegardee, CourrierCustomViews
  public deletePersonalParams(id: number, type: string): Observable<void> {
    return this.http.delete<void>(`${this.iCourrierURL}/Me/Configurations/PersonalParams/${id}?type=${type}`);
  }

  //#endregion

  //#region Notif mails

  public markReadNotification(data: { ids: string[] }): Observable<void> {
    return this.http.patch<void>(`${this.iCourrierURL}/Me/Notifications/MarkAsReaded`, data);
  }

  public getNotifications(data: NotificationMailParams): Observable<NotifMailSearchDTOPaginate> {
    let params = new HttpParams();
    if (data) {
      Object.keys(data).forEach((key) => (params = params.set(key, data[key])));
    }
    return this.http.get<NotifMailSearchDTOPaginate>(`${this.iCourrierURL}/Me/Notifications/Search?${params}`);
  }

  public getAllNotifications(): Observable<NotifMailsDTO> {
    return this.http.get<NotifMailsDTO>(`${this.iCourrierURL}/Me/Configurations/NotifMail`);
  }

  public postNotifications(data: NotifMailParams): Observable<void> {
    return this.http.post<void>(`${this.iCourrierURL}/Me/Configurations/NotifMail`, data);
  }

  public deleteNotifications(): Observable<void> {
    return this.http.delete<void>(`${this.iCourrierURL}/Me/Configurations/NotifMail`);
  }

  //#endregion

  //#region InCharge
  public getUsersInCharge(): Observable<number[]> {
    return this.http.get<number[]>(`${this.iCourrierURL}/Me/InCharge`);
  }
  //#endregion
}
